import { FormGroup } from "@mui/material";
import { ButtonArrow } from "@thrivetribe/mvf.assets.icons.button-arrow";
import { Button } from "@thrivetribe/mvf.ui.atoms.button";
import { FormikNumberInput } from "@thrivetribe/mvf.ui.atoms.formik-number-input";
import { FormikTextInput } from "@thrivetribe/mvf.ui.atoms.formik-text-input";
import { LoadingSpinner } from "@thrivetribe/mvf.ui.molecules.loading-spinner";
import {
  OnboardingPage,
  OnboardingPageBody,
  OnboardingPageContent,
  OnboardingPageCopy,
  OnboardingPageFooter,
} from "@thrivetribe/mvf.ui.organisms.onboarding-page";
import { Form as FormikForm, Formik } from "formik";
import { signupNavLink } from "Onboarding/utils";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { EmergencyContactValidationSchema } from "../OnboardingValidations";
import "./EmergencyContact.scss";
import { useGetUserQuery, useUpdateUserMutation } from "redux/userApiSlice";
import { useUser } from "components/user/UserContext";
import { Gtag } from "configs/ReactGa";
import { ReactGa } from "ui/atoms/ReactGa";
import { ImagesUrl } from "configs/EnvironmentConfig";

type FormValueTypes = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
};

const EmergencyContact = () => {
  let navigate = useNavigate();
  let user = useUser();
  let { data: userObj, isLoading } = useGetUserQuery(user?.userId);
  let [updateProfile] = useUpdateUserMutation({
    fixedCacheKey: user?.userId,
  });

  const updateProfileData = async (firstName: string, lastName: string, phoneNumber: string) => {
    if (userObj) {
      const updatedProfile = {
        ...userObj,
        personalDetails: { ...userObj.personalDetails, nextOfKin: { firstName, lastName, phoneNumber } },
      };

      await updateProfile(updatedProfile)
        .unwrap()
        .then(() => {
          Gtag("event", "emergency_contact_set", {
            user_id: user.userId,
            event_category: "account setup",
          });
          navigate("/portal-setup/any-health-conditions");
        });
    }
  };

  const initialValues: FormValueTypes = {
    firstName: userObj?.personalDetails?.nextOfKin?.firstName ?? "",
    lastName: userObj?.personalDetails?.nextOfKin?.lastName ?? "",
    phoneNumber: userObj?.personalDetails?.nextOfKin?.phoneNumber ?? "",
  };

  const handleSubmit = async (values: FormValueTypes) => {
    updateProfileData(values.firstName, values.lastName, values.phoneNumber);
  };

  const handleBack = () => {
    navigate("/portal-setup/ethnicity");
  };

  if (isLoading) {
    return <LoadingSpinner fullscreen={true} />;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={EmergencyContactValidationSchema}
      validateOnChange={false}
    >
      <FormikForm>
        <Helmet>
          {user.isLegacy ? <title>Set up - Emergency contact</title> : <title>Sign up - Emergency contact</title>}
        </Helmet>
        <OnboardingPage
          user={user.displayName}
          links={signupNavLink}
          image={`${ImagesUrl}/football.webp`}
          title={user.isLegacy ? "Set up" : "Sign up"}
          onBackClick={handleBack}
        >
          <OnboardingPageContent hasStickyFooter centralizeContent>
            <OnboardingPageBody>
              <OnboardingPageCopy>
                <h1>Who's your emergency contact?</h1>
                {user.isLegacy ? (
                  <p>You might have told us this before but fill us in again so that we're all up to date.</p>
                ) : (
                  <p>Let us know who to call in an emergency.</p>
                )}
              </OnboardingPageCopy>
              <div className="emergency-contact">
                <FormGroup className="onboarding-page__content--group">
                  <FormikTextInput id="firstName" name="firstName" label="First name" />
                </FormGroup>
                <FormGroup className="onboarding-page__content--group">
                  <FormikTextInput id="lastName" name="lastName" label="Last name" />
                </FormGroup>
                <FormGroup className="onboarding-page__content--group">
                  <FormikNumberInput
                    pattern={/^([0-9]{0,11})/g}
                    id="phoneNumber"
                    name="phoneNumber"
                    label="Phone number"
                  />
                </FormGroup>
              </div>
            </OnboardingPageBody>
            <OnboardingPageFooter>
              <Button type="submit" variant="primary" color="primary">
                Continue
                <ButtonArrow />
              </Button>
            </OnboardingPageFooter>
          </OnboardingPageContent>
        </OnboardingPage>
        <ReactGa
          eventName="emergency_contact_requested"
          eventParams={{ user_id: user.userId, event_category: "account setup" }}
        />
      </FormikForm>
    </Formik>
  );
};

export default EmergencyContact;
